import { formatEUR, formatDate } from "../lib/format";

const Section = ({ title, children, empty = false }) => (
  <section className={`mt-8 break-inside-avoid ${empty ? "print-hide" : ""}`}>
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0AA09E] border-b border-[#E5E7EB] pb-2 mb-3">{title}</h2>
    {children}
  </section>
);

const Paragraphs = ({ text, empty }) => (
  text ? text.split(/\n\s*\n/).map((p, i) => <p key={i} className="text-[13px] leading-relaxed text-[#1F242E] mb-3">{p.trim()}</p>)
    : <p className="text-[13px] italic text-[#94A3B8] print-hide">{empty}</p>
);

const Bar = ({ label, value, max, color, note, testid }) => (
  <div className="mb-3">
    <div className="flex justify-between text-[12px] mb-1">
      <span className="font-medium text-[#1F242E]">{label}</span>
      <span className="font-display font-bold text-[#1F242E]" data-testid={testid}>{formatEUR(value)}</span>
    </div>
    <div className="h-3 rounded-full bg-[#F1F5F9] overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, background: color }} />
    </div>
    {note && <div className="text-[10px] text-[#5B6371] mt-1">{note}</div>}
  </div>
);

export const ProfileDocument = ({ profile: p, figures: f }) => {
  const facts = [["Rechtsform", p.legal_form], ["Geschäftsführung", p.managing_director], ["Gegründet", p.founded],
    ["Anschrift", p.address], ["Kontakt", p.contact], ["Website", p.website], ["Auftraggeber", p.clients]].filter(([, v]) => v);
  const services = (p.services || "").split(",").map((s) => s.trim()).filter(Boolean);
  const max = Math.max(f.revenue_year, f.forecast_next);
  return (
    <article id="profile-doc" className="bg-white border border-[#E5E7EB] rounded-lg shadow-sm px-10 py-10 max-w-[820px] w-full mx-auto" data-testid="profile-document">
      <header className="flex items-start justify-between gap-6 border-b-2 border-[#0AA09E] pb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#5B6371]">Unternehmensprofil</div>
          <div className="font-display text-3xl font-bold text-[#1F242E] mt-1" data-testid="doc-company-name">{p.company_name} {p.legal_form}</div>
          <div className="text-[12px] text-[#5B6371] mt-1">Stand: {formatDate(f.generated_at)}</div>
        </div>
        <img src="/we-logo.png" alt="Logo" className="h-14 w-14 object-contain" />
      </header>

      <Section title="Wer wir sind" empty={!p.intro_text}><Paragraphs text={p.intro_text} empty="Noch kein Text – links „Mit KI schreiben“ klicken." /></Section>

      {services.length > 0 && (
        <Section title="Unsere Leistungen">
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {services.map((s) => (
              <li key={s} className="border border-[#E5E7EB] rounded-md px-3 py-3 text-[12px] font-medium text-[#1F242E] bg-[#F8FAFC]">{s}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="So verdienen wir Geld" empty={!p.business_text}><Paragraphs text={p.business_text} empty="Noch kein Text – links „Mit KI schreiben“ klicken." /></Section>

      <Section title={`Umsatz ${f.year} und Prognose ${f.next_year}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="rounded-md bg-[#E6F7F7] px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Jahresumsatz {f.year}</div>
            <div className="font-display text-3xl font-bold text-[#1F242E] mt-1" data-testid="doc-revenue-year">{formatEUR(f.revenue_year)}</div>
            <div className="text-[10px] text-[#5B6371] mt-1">davon bereits eingegangen: {formatEUR(f.realized_year)}</div>
          </div>
          <div className="rounded-md bg-[#FFF1EF] px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest text-[#5B6371]">Prognose {f.next_year}</div>
            <div className="font-display text-3xl font-bold text-[#1F242E] mt-1" data-testid="doc-forecast">{formatEUR(f.forecast_next)}</div>
            <div className="text-[10px] text-[#5B6371] mt-1">bei {Number(f.growth_pct).toLocaleString("de-DE")} % Wachstum</div>
          </div>
        </div>
        <Bar label={`Umsatz ${f.year}`} value={f.revenue_year} max={max} color="#0AA09E" testid="doc-bar-year"
          note={`Geschäftstätigkeit ab ${f.first_month.slice(5, 7)}/${f.first_month.slice(0, 4)} · Ø ${formatEUR(f.avg_month)} pro Monat`} />
        <Bar label={`Prognose ${f.next_year}`} value={f.forecast_next} max={max} color="#FF6B5C" testid="doc-bar-forecast"
          note={`Hochrechnung: Ø-Monatsumsatz × 12 = ${formatEUR(f.annualized)}, zzgl. ${Number(f.growth_pct).toLocaleString("de-DE")} % Wachstum${f.booked_next > 0 ? ` · bereits gebucht für ${f.next_year}: ${formatEUR(f.booked_next)}` : ""}`} />
      </Section>

      {facts.length > 0 && (
        <Section title="Unternehmensdaten">
          <dl className="grid grid-cols-[140px_1fr] gap-y-1.5 text-[12px]">
            {facts.flatMap(([k, v]) => [
              <dt key={`${k}-k`} className="text-[#5B6371]">{k}</dt>,
              <dd key={`${k}-v`} className="text-[#1F242E]">{v}</dd>,
            ])}
          </dl>
        </Section>
      )}

      <footer className="mt-10 pt-4 border-t border-[#E5E7EB] text-[9px] text-[#94A3B8] leading-relaxed">
        Umsatz {f.year}: vertraglich vereinbarte Vergütungen mit Fälligkeit in {f.year} (bereits eingegangen und noch ausstehend).
        Die Prognose ist eine Hochrechnung auf Basis des bisherigen Geschäftsverlaufs und stellt keine Garantie dar.
      </footer>
    </article>
  );
};
